import React, { useRef } from 'react';
import cn from 'classnames';
import PageLayout from 'components/common/PageLayout';
import TopSection from 'components/common/TopSection';
import InfoBoxesList, { InfoBoxType } from 'components/common/InfoBoxesList';
import translator from 'utils/translator';
import css from './EditorPage.module.scss';
import images from 'assets/images';
import ScrollSection from 'components/common/ScrollSection';
import useResponsiveQuery from 'utils/useResponsiveQuery';
import { FIGMA_TO_STORYCARDS_ROUTE, SUBSCRIPTION_PLANS_PAGE } from 'components/constants';
import InfoSection from 'components/common/InfoSection';
import { LinkWithQuery } from 'components/common/LinkWithQuery';

type Props = {};

type FeatureItem = {
  title: string;
  text: string;
};

type FeatureGroupDefinition = {
  eyebrow: string;
  title: string;
  text: string;
  itemTitles: string[];
};

const _t = (path: string) => translator.text(`editorPage.${path}`);

const featureGroupDefinitions: FeatureGroupDefinition[] = [
  {
    eyebrow: '01',
    title: 'Start with intent',
    text: 'Move from AI or Figma into a fully editable canvas without losing the thinking behind the design.',
    itemTitles: ['Figma Integration', 'AI Chat, Side by Side', 'Pixel-Perfect Design Control', 'Drag and Drop'],
  },
  {
    eyebrow: '02',
    title: 'Design with precision',
    text: 'Control composition, typography and responsive behavior at every screen size.',
    itemTitles: [
      'Precision at Every Breakpoint',
      'Auto Layouts, Grids, and Boxes',
      'Use Your Own Fonts',
      'Gradients',
      'Tailor Your Content by Platform',
      'Text Scaling',
    ],
  },
  {
    eyebrow: '03',
    title: 'Build interaction and behavior',
    text: 'Design the states, paths and feedback that turn a layout into a complete interactive product.',
    itemTitles: [
      'Personalized Journeys',
      'Overlays',
      'Sliders',
      'Animations',
      'Counters',
      'Timer',
      'Design Forms and Fields',
      'Answers Component',
      'State-Based Answers',
      'Interactive Feedback Messages',
      'Create Global and Sticky Elements',
      'Advanced Conditional Logic',
      'Dynamic Content and Data Binding',
    ],
  },
  {
    eyebrow: '04',
    title: 'Create and improve with AI',
    text: 'Generate assets, test alternatives and keep every output editable by the design team.',
    itemTitles: ['AI-Powered Image Creation', 'Generative Video Creation', 'Integrated A/B Testing Tools'],
  },
  {
    eyebrow: '05',
    title: 'Scale the design system',
    text: 'Turn the brand system into reusable rules that every team can create within safely.',
    itemTitles: [
      'Approved AI-Ready Templates',
      'Brand Rules and Libraries',
      'Reusable Design Systems',
      'Protected Editing',
      'Controlled Publishing',
      'Revision History',
    ],
  },
  {
    eyebrow: '06',
    title: 'Publish production-ready experiences',
    text: 'Prepare every product for real audiences, real systems and every device before it goes live.',
    itemTitles: [
      'Crop and Resize Images',
      'Image Auto-Optimize',
      'Image Serve Auto Format',
      'Deliver the Best Image Size by Device',
      'Add Share Buttons',
      'Custom CSS',
      'Add Your Own Code',
      'SEO Tools',
      'Data Integrations',
      'Accessibility Features',
      'Multilingual and Localized Versions',
      'Publish Anywhere',
      'Auto Dark Mode Version',
      'Custom Dark Mode Version',
      'User Toggle for Dark Mode',
      'Dark Mode by URL Parameter',
    ],
  },
];

const infoItems: Array<Array<InfoBoxType>> = [
  [
    {
      title: _t('infoItems.designItTitle'),
      text: _t('infoItems.designItText'),
      icon: 'pensil',
    },
    {
      title: _t('infoItems.notMoreTitle'),
      text: _t('infoItems.notMoreText'),
      icon: 'star',
    },
  ],
  [
    {
      title: _t('infoItems.designResponsiveTitle'),
      text: _t('infoItems.designResponsiveText'),
      icon: 'responsive',
    },
    {
      title: _t('infoItems.fontsTitle'),
      text: _t('infoItems.fontsText'),
      icon: 'font',
    },
  ],
  [
    {
      title: _t('infoItems.contentTitle'),
      text: _t('infoItems.contentText'),
      icon: 'plus',
    },
    {
      title: _t('infoItems.platformTitle'),
      text: _t('infoItems.platformText'),
      icon: 'mobile',
    },
  ],
  [
    {
      title: _t('infoItems.createReusableTitle'),
      text: _t('infoItems.createReusableText'),
      icon: 'symbol',
    },
    {
      title: _t('infoItems.gridTitle'),
      text: _t('infoItems.gridText'),
      icon: 'grid',
    },
  ],
  [
    {
      title: _t('infoItems.globalStickyTitle'),
      text: _t('infoItems.globalStickyText'),
      icon: 'layers',
    },
    {
      title: _t('infoItems.animationTitle'),
      text: _t('infoItems.animationText'),
      icon: 'animation',
    },
  ],
  [
    {
      title: _t('infoItems.shareTitle'),
      text: _t('infoItems.shareText'),
      icon: 'share',
    },
    {
      title: _t('infoItems.seoTitle'),
      text: _t('infoItems.seoText'),
      icon: 'search',
    },
  ],
  [
    {
      title: _t('infoItems.userTitle'),
      text: _t('infoItems.userText'),
      icon: 'user',
    },
    {
      title: _t('infoItems.ownCodeTitle'),
      text: _t('infoItems.ownCodeText'),
      icon: 'embed',
    },
  ],
  [
    {
      title: _t('infoItems.marketingTitle'),
      text: _t('infoItems.marketingText'),
      icon: '',
    },
    {
      title: _t('infoItems.revisionTitle'),
      text: _t('infoItems.revisionText'),
      icon: '',
    },
  ],
];

const EditorPage: React.FC<Props> = () => {
  const videoRef = useRef<HTMLVideoElement>(document.createElement('video'));
  const isMobile = useResponsiveQuery();
  const CTALabel = 'Start Creating';
  const videoSrc = 'https://assts.stories.sc/website/desktop/design.mp4';
  const videoSrcMob = 'https://assts.stories.sc/website/mobile/design.mp4';
  const videoPoster = 'https://assts.stories.sc/website/poster/design.jpg';
  const infoTexts = _t('infoSection') as FeatureItem[];
  const assignedFeatureTitles = new Set(featureGroupDefinitions.flatMap((group) => group.itemTitles));
  const ungroupedFeatures = infoTexts.filter((item) => !assignedFeatureTitles.has(item.title));
  const featureGroups = [
    ...featureGroupDefinitions.map((group) => ({
      ...group,
      items: group.itemTitles
        .map((title) => infoTexts.find((item) => item.title === title))
        .filter((item): item is FeatureItem => Boolean(item)),
    })),
    ...(ungroupedFeatures.length
      ? [
          {
            eyebrow: '07',
            title: 'More studio capabilities',
            text: 'Additional tools for creating and operating complete digital experiences.',
            itemTitles: [],
            items: ungroupedFeatures,
          },
        ]
      : []),
  ];

  return (
    <PageLayout className={css.editorPage} isFullWidth isWhiteHeader={false}>
      <div className={`flex flex-col items-center `}>
        <TopSection
          className={css.studioHero}
          title={_t('topSection.title')}
          subtitle={_t('topSection.subtitle')}
          ctaLabel={CTALabel}
          ctaUrl={SUBSCRIPTION_PLANS_PAGE}
        />
        <section className={css.videoSection}>
          <div className={`${css.videoWrap} relative`}>
            <video
              playsInline
              autoPlay
              ref={videoRef}
              loop
              muted
              disableRemotePlayback
              src={isMobile ? videoSrcMob : videoSrc}
              poster={videoPoster}
            />
            <div
              className={cn(css.text, 'text-2')}
              dangerouslySetInnerHTML={{ __html: _t('videoSectionUnderText') }}
            ></div>
          </div>
        </section>
        <div className={`${css.scrollSectionContent} flex flex-col items-center`}>
          <ScrollSection sectionData={_t('scrollSection')} />
        </div>
        <section className={css.capabilitiesSection}>
          <section className={css.designerStorySection}>
            <div className={css.designerStory}>
              <div className={css.storyIntro}>
                <span>{_t('designerStory.eyebrow')}</span>
                <h2>{_t('designerStory.title')}</h2>
                <p>{_t('designerStory.text')}</p>
              </div>
              <div className={css.storyPillars}>
                {(_t('designerStory.pillars') as FeatureItem[]).map((pillar, index) => (
                  <article key={pillar.title}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.text}</p>
                    {index === 1 && (
                      <LinkWithQuery className={css.storyLink} to={FIGMA_TO_STORYCARDS_ROUTE}>
                        Explore Figma to Storycards →
                      </LinkWithQuery>
                    )}
                  </article>
                ))}
              </div>
            </div>
          </section>
          <section className={css.featureIntroSection}>
            <div className={css.featureIntro}>
              <span>{_t('featureSection.eyebrow')}</span>
              <h2>{_t('featureSection.title')}</h2>
              <p>{_t('featureSection.text')}</p>
            </div>
          </section>
          <div className={css.featureCatalog}>
            {featureGroups.map((group, index) => (
              <section
                className={cn(
                  css.featureGroup,
                  (index === 2 || index === 3) && css.featureGroupMuted,
                  index === 4 && css.featureGroupMint,
                )}
                key={group.title}
              >
                <header>
                  <span>{group.eyebrow}</span>
                  <div>
                    <h3>{group.title}</h3>
                    <p>{group.text}</p>
                  </div>
                </header>
                <div className={css.featureSection}>
                  <InfoSection items={group.items} />
                </div>
              </section>
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default EditorPage;
